import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Canceler } from "axios";
import { useAppTable, useFormContainerState, useTrans } from "../../../hooks";
import {
    VStack,
    HStack,
    useToast,
    Box,
    ButtonGroup,
    Button,
    IconButton,
} from "@chakra-ui/react";
import { updatePageIndex } from "ka-table/actionCreators";
import {
    Calendar,
    Culture,
    DateLocalizer,
    dayjsLocalizer,
    EventProps,
    Formats,
    Messages,
    View,
} from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isLeapYear from "dayjs/plugin/isLeapYear";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import minMax from "dayjs/plugin/minMax";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import { AppSearch } from "../../../containers";
import {
    AppCalendarCustomEvent,
    AppFilterButton,
    AppIcon,
    AppPageContent,
    AppPageHeader,
    AppTooltip,
    CalendarDataType,
} from "../../../components";
import { MyInternshipList as ListComponent } from "./MyInternshipList";
import { MyInternshipForm as FormComponent } from "./MyInternshipForm";
import { MyInternshipFilter as FilterComponent } from "./MyInternshipFilter";
import {
    Company,
    InternshipScheduleSeat as Entity,
    InternshipScheduleSeatApi as EntityApi,
    Internship,
    InternshipSchedule,
    InternshipScheduleApi,
    Timesheet,
    TimesheetApi,
    TrainingYear,
} from "../../../apis";
import { APPC, cPath, LSC, UTIL } from "../../../config";
import { useAuthData } from "../../../contexts";

dayjs.extend(isBetween);
dayjs.extend(isLeapYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(minMax);
dayjs.locale(
    localStorage.getItem(LSC.APP_USER_LOCALE) || APPC.APP_DEFAULT_LOCALE
);
dayjs.extend(timezone);

export const MyInternshipPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const navigate = useNavigate();
    const formContainerState = useFormContainerState();
    const { grant } = useAuthData();

    // state & const
    const [search, setSearch] = useState("");
    const [filterData, setFilterData] = useState<any>({});
    const [loadingList, setLoadingList] = useState(true);
    const [list, setList] = useState<Entity[]>([]);
    const [isDetailPage, setIsDetailPage] = useState(false);
    const [viewType, setViewType] = useState<
        (typeof APPC.FE.ViewType)[keyof typeof APPC.FE.ViewType]
    >(APPC.FE.ViewType.VIEW_TYPE_CALENDAR);
    const VIEWTYPE = APPC.FE.ViewType;
    const localizer = dayjsLocalizer(dayjs);
    const DnDCalendar = withDragAndDrop<CalendarDataType>(Calendar);
    const [events, setEvents] = useState<CalendarDataType[]>([]);
    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [view, setView] = useState<View>("week");

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const {
        table,
        pageIndex,
        pagesCount,
        pageSize,
        sortColumn,
        sortOrder,
        setPagesCount,
    } = useAppTable("internshipSchedule.startDate", "desc");

    const paging = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize,
        pageIndex,
        pagesCount,
    };

    // handler
    const fetchListData = async () => {
        setLoadingList(true);
        const params: any = {
            [`order[${sortColumn}]`]: sortOrder,
            ...filterData,
        };
        if (viewType === VIEWTYPE.VIEW_TYPE_LIST) {
            params.itemsPerPage = pageSize;
        }
        if (search) {
            params["internship_schedule_seat_search"] = search;
        }
        if (viewType === VIEWTYPE.VIEW_TYPE_CALENDAR) {
            const month = dayjs(currentDate).format("YYYY-MM");
            params.pagination = false;
            params["internship_schedule_seat_month"] = month;
        }

        await EntityApi.getCollectionMyInternship<Entity>(
            viewType === VIEWTYPE.VIEW_TYPE_LIST ? pageIndex + 1 : 1,
            params,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        )
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    if (viewType === VIEWTYPE.VIEW_TYPE_LIST) {
                        setPagesCount(
                            Math.ceil(response.totalItems / pageSize)
                        );
                        setList(response.items);
                    } else {
                        let calendarEvents: CalendarDataType[] = [];
                        response.items.forEach((item: Entity) => {
                            calendarEvents = [
                                ...calendarEvents,
                                ...getEventsFromInternshipScheduleSeat(item),
                            ];
                        });
                        setEvents(calendarEvents);
                    }
                }
            })
            .finally(() => setLoadingList(false));
    };

    const getEventsFromInternshipScheduleSeat = (
        ist: Entity
    ): CalendarDataType[] => {
        const schedule = ist.internshipSchedule as InternshipSchedule;
        const internship = schedule?.internship as Internship;
        const company = internship?.company as Company;
        const isDetail = !!schedule.isScheduleDetail;

        const calendarEvents: CalendarDataType[] = [];

        if (isDetail) {
            schedule.timeSlots.forEach((cw) => {
                cw.forEach((cd) => {
                    if (cd.isChecked) {
                        calendarEvents.push({
                            id: ist.id,
                            internshipId: internship?.id,
                            internshipScheduleId: schedule?.id,
                            companyName: company?.name,
                            internshipName: internship?.name,
                            internshipScheduleName: schedule?.name,
                            breakDuration: `${schedule?.breakDuration} ${t("cmn:label.min")}`,
                            totalTime: `${schedule?.totalTime / 60} ${t("cmn:label.hours")}`,
                            start: dayjs(
                                `${dayjs(cd.date).format(
                                    APPC.APP_HTML_DATE_FORMAT
                                )} ${dayjs
                                    .tz(cd.startTime, "UTC")
                                    .format(APPC.APP_HTML_TIME_FORMAT)}`
                            ).toDate(),
                            end: dayjs(
                                `${dayjs(cd.date).format(
                                    APPC.APP_HTML_DATE_FORMAT
                                )} ${dayjs
                                    .tz(cd.endTime, "UTC")
                                    .format(APPC.APP_HTML_TIME_FORMAT)}`
                            ).toDate(),
                        } as CalendarDataType);
                    }
                });
            });
            return calendarEvents;
        }

        let currentDate = dayjs(schedule.startDate);
        const weekDays = schedule?.weekDays;
        while (currentDate.toDate() <= dayjs(schedule.endDate).toDate()) {
            if (weekDays[UTIL.getWeekDayName(currentDate.format("d"))]) {
                calendarEvents.push({
                    id: ist.id,
                    internshipId: internship?.id,
                    internshipScheduleId: schedule?.id,
                    companyName: company?.name,
                    internshipName: internship?.name,
                    internshipScheduleName: schedule?.name,
                    breakDuration: `${schedule?.breakDuration} ${t("cmn:label.min")}`,
                    totalTime: `${schedule?.totalTime / 60} ${t("cmn:label.hours")}`,
                    start: dayjs(
                        `${currentDate.format(
                            APPC.APP_HTML_DATE_FORMAT
                        )} ${dayjs
                            .tz(schedule.startTime, "UTC")
                            .format(APPC.APP_HTML_TIME_FORMAT)}`
                    ).toDate(),
                    end: dayjs(
                        `${currentDate.format(
                            APPC.APP_HTML_DATE_FORMAT
                        )} ${dayjs
                            .tz(schedule.endTime, "UTC")
                            .format(APPC.APP_HTML_TIME_FORMAT)}`
                    ).toDate(),
                    color: `${(schedule?.trainingYear as TrainingYear)?.color}`,
                } as CalendarDataType);
            }
            currentDate = dayjs(currentDate).add(1, "days");
        }
        return calendarEvents;
    };

    const handleSelectEvent = useCallback((event: CalendarDataType) => {
        setIsDetailPage(true);
        formContainerState.open(event.id);
    }, []);

    const calendarActionClickHandler = (key: string, id: number) => {
        switch (key) {
            case "students": {
                navigate(
                    cPath("ADMIN.STUDENTS_PAGE", {
                        internshipScheduleSeatId: id,
                    })
                );
                break;
            }
            case "assignStudents":
                navigate(
                    cPath("ADMIN.STUDENT_PAGE", {
                        internshipScheduleSeatId: id,
                    })
                );
                break;
            case "timesheet":
                navigate(
                    cPath("ADMIN.TIMESHEET_PAGE", {
                        internshipScheduleSeatId: id,
                    })
                );
                break;
            case "timesheet-report":
                downloadReportHandler(id);
                break;
            case "skills":
                navigate(
                    cPath("ADMIN.STUDENT_SKILL_PAGE", {
                        internshipScheduleSeatId: id,
                    })
                );
                break;
        }
    };

    const messages: Messages<CalendarDataType> = useMemo(
        () => ({
            today: t("cmn:label.today"),
            previous: t("cmn:label.previous"),
            next: t("cmn:label.next"),
            month: t("cmn:label.month"),
            week: t("cmn:label.week"),
            day: t("cmn:label.day"),
            agenda: t("cmn:label.agenda"),
            date: t("cmn:label.date"),
            time: t("cmn:label.time"),
            event: t("cmn:label.event"),
        }),
        []
    );

    const formats: Formats = useMemo(
        () => ({
            dateFormat: "DD",
            weekdayFormat: (
                date: Date,
                culture?: Culture,
                localizer?: DateLocalizer
            ) => {
                return localizer?.format(date, "ddd", culture) ?? "";
            },
            monthHeaderFormat: (
                date: Date,
                culture?: Culture,
                localizer?: DateLocalizer
            ) => localizer?.format(date, "MMM - YYYY", culture) ?? "",
            dayFormat: (
                date: Date,
                culture?: Culture,
                localizer?: DateLocalizer
            ) => localizer?.format(date, "ddd - DD/MM", culture) ?? "",
            dayRangeHeaderFormat: (
                range: { start: Date; end: Date },
                culture?: Culture,
                localizer?: DateLocalizer
            ) => {
                const start = localizer?.format(range.start, "MMM DD", culture);
                const end = localizer?.format(range.end, "MMM DD", culture);
                return `${start} - ${end}`;
            },
            timeGutterFormat: (
                date: Date,
                culture?: Culture,
                localizer?: DateLocalizer
            ) => localizer?.format(date, APPC.APP_TIME_FORMAT, culture) ?? "",
            eventTimeRangeFormat: (
                range: { start: Date; end: Date },
                culture?: Culture,
                localizer?: DateLocalizer
            ) => {
                const startTime = localizer?.format(
                    range.start,
                    APPC.APP_TIME_FORMAT,
                    culture
                );
                const endTime = localizer?.format(
                    range.end,
                    APPC.APP_TIME_FORMAT,
                    culture
                );
                return `${startTime} - ${endTime}`;
            },
            dayHeaderFormat: (
                date: Date,
                culture?: Culture,
                localizer?: DateLocalizer
            ) =>
                localizer?.format(
                    date,
                    `ddd - ${APPC.APP_DATE_FORMAT}`,
                    culture
                ) ?? "",
            agendaDateFormat: (
                date: Date,
                culture?: Culture,
                localizer?: DateLocalizer
            ) => localizer?.format(date, APPC.APP_DATE_FORMAT, culture) ?? "",
            agendaTimeRangeFormat: (
                range: { start: Date; end: Date },
                culture?: Culture,
                localizer?: DateLocalizer
            ) => {
                const startTime = localizer?.format(
                    range.start,
                    APPC.APP_TIME_FORMAT,
                    culture
                );
                const endTime = localizer?.format(
                    range.end,
                    APPC.APP_TIME_FORMAT,
                    culture
                );
                return `${startTime} - ${endTime}`;
            },
            agendaHeaderFormat: (
                range: { start: Date; end: Date },
                culture?: Culture,
                localizer?: DateLocalizer
            ) => {
                const startTime = localizer?.format(
                    range.start,
                    APPC.APP_DATE_FORMAT,
                    culture
                );
                const endTime = localizer?.format(
                    range.end,
                    APPC.APP_DATE_FORMAT,
                    culture
                );
                return `${startTime} - ${endTime}`;
            },
        }),
        []
    );

    const downloadReportHandler = (internshipScheduleId: number) => {
        if (internshipScheduleId > 0) {
            TimesheetApi.postCollectionReport<BlobPart, Timesheet>({
                internshipSchedule:
                    InternshipScheduleApi.toResourceIRI(internshipScheduleId),
            } as Timesheet).then(({ response }) => {
                if (response !== null) {
                    const fileURL = URL.createObjectURL(
                        new Blob([response], { type: "application/pdf" })
                    );
                    const alink = document.createElement("a");
                    alink.href = fileURL;
                    alink.download = "timesheet-report.pdf";
                    alink.click();
                }
            });
        }
    };

    const actionClickHandler = (key: string, data?: Entity) => {
        if (!data) {
            return;
        }
        switch (key) {
            case "timesheet":
                navigate(
                    cPath("ADMIN.TIMESHEET_PAGE", {
                        internshipScheduleSeatId: data.id,
                    })
                );
                break;
            case "timesheet-report":
                downloadReportHandler(
                    (data?.internshipSchedule as InternshipSchedule)?.id
                );
                break;
            case "students": {
                navigate(
                    cPath("ADMIN.STUDENTS_PAGE", {
                        internshipScheduleSeatId: data?.id,
                    })
                );
                break;
            }
            case "assignStudents":
                navigate(
                    cPath("ADMIN.STUDENT_PAGE", {
                        internshipScheduleSeatId: data.id,
                    })
                );
                break;
            case "skills":
                navigate(
                    cPath("ADMIN.STUDENT_SKILL_PAGE", {
                        internshipScheduleSeatId: data.id,
                    })
                );
                break;
            case "detail":
                setIsDetailPage(true);
                formContainerState.open(data?.id);
                break;
        }
    };

    // useEffect
    useEffect(() => {
        fetchListData();
    }, [
        viewType,
        currentDate,
        pageIndex,
        pageSize,
        sortColumn,
        sortOrder,
        search,
        filterData,
    ]);

    useEffect(() => {
        if (pageIndex > 0) {
            table.dispatch(updatePageIndex(0));
        }
    }, [filterData, viewType]);

    return (
        <>
            <AppPageHeader title={t("padm.InternshipPage:text.pageTitle")}>
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    <AppSearch
                        onSearch={(value) => {
                            setSearch(value);
                            if (pageIndex > 0) {
                                table.dispatch(updatePageIndex(0));
                            }
                        }}
                    />
                    <ButtonGroup size="sm" isAttached variant="outline">
                        <Button
                            className="btn-icon-md"
                            size="md"
                            isActive={viewType === VIEWTYPE.VIEW_TYPE_CALENDAR}
                            onClick={() =>
                                setViewType(VIEWTYPE.VIEW_TYPE_CALENDAR)
                            }
                        >
                            <AppIcon name="icl-calendar" w="1.25rem" />
                        </Button>
                        <Button
                            className="btn-icon-md"
                            size="md"
                            isActive={viewType === VIEWTYPE.VIEW_TYPE_LIST}
                            onClick={() => setViewType(VIEWTYPE.VIEW_TYPE_LIST)}
                        >
                            <AppIcon name="icl-list" w="1.25rem" />
                        </Button>
                    </ButtonGroup>
                    <FilterComponent
                        filterData={filterData}
                        setFilterData={setFilterData}
                    >
                        <Box role="button">
                            <AppFilterButton filterData={filterData} />
                        </Box>
                    </FilterComponent>
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                <>
                    {viewType === VIEWTYPE.VIEW_TYPE_CALENDAR ? (
                        <Box h={"full"} w={"full"} p={6}>
                            <DnDCalendar
                                localizer={localizer}
                                events={events}
                                date={currentDate}
                                view={view}
                                defaultView="month"
                                onNavigate={(newDate) =>
                                    setCurrentDate(newDate)
                                }
                                onView={(newView) => setView(newView)}
                                dayLayoutAlgorithm="no-overlap"
                                showMultiDayTimes
                                components={{
                                    event: (
                                        props: EventProps<CalendarDataType>
                                    ) => (
                                        <AppCalendarCustomEvent
                                            {...props}
                                            view={view}
                                            renderActions={(event) => {
                                                if (grant.isStudent) {
                                                    return (
                                                        <HStack
                                                            p={1}
                                                            gap={1}
                                                            wrap={"wrap"}
                                                        >
                                                            <AppTooltip
                                                                label={t(
                                                                    "padm.InternshipSchedulePage:text.option.timesheet"
                                                                )}
                                                            >
                                                                <IconButton
                                                                    aria-label="timesheet"
                                                                    variant={
                                                                        "unstyled"
                                                                    }
                                                                    icon={
                                                                        <AppIcon
                                                                            name="icl-spreadsheet"
                                                                            w="1rem"
                                                                        />
                                                                    }
                                                                    size="xs"
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        calendarActionClickHandler(
                                                                            "timesheet",
                                                                            event.id
                                                                        );
                                                                    }}
                                                                />
                                                            </AppTooltip>
                                                            <AppTooltip
                                                                label={t(
                                                                    "padm.MyInternshipPage:text.option.timesheetReport"
                                                                )}
                                                            >
                                                                <IconButton
                                                                    aria-label="timesheet-report"
                                                                    variant={
                                                                        "unstyled"
                                                                    }
                                                                    icon={
                                                                        <AppIcon
                                                                            name="icl-file-pdf"
                                                                            w="1rem"
                                                                        />
                                                                    }
                                                                    size="xs"
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        calendarActionClickHandler(
                                                                            "timesheet-report",
                                                                            event.internshipScheduleId
                                                                        );
                                                                    }}
                                                                />
                                                            </AppTooltip>
                                                            <AppTooltip
                                                                label={t(
                                                                    "padm.InternshipSchedulePage:text.option.skill"
                                                                )}
                                                            >
                                                                <IconButton
                                                                    aria-label="skills"
                                                                    variant={
                                                                        "unstyled"
                                                                    }
                                                                    icon={
                                                                        <AppIcon
                                                                            name="icl-lightbulb"
                                                                            w="1rem"
                                                                        />
                                                                    }
                                                                    size="xs"
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        calendarActionClickHandler(
                                                                            "skills",
                                                                            event.id
                                                                        );
                                                                    }}
                                                                />
                                                            </AppTooltip>
                                                        </HStack>
                                                    );
                                                }
                                                return (
                                                    <HStack>
                                                        <AppTooltip
                                                            label={t(
                                                                "padm.InternshipSchedulePage:text.option.students"
                                                            )}
                                                        >
                                                            <IconButton
                                                                aria-label="students"
                                                                variant={
                                                                    "unstyled"
                                                                }
                                                                icon={
                                                                    <AppIcon
                                                                        name="icl-user"
                                                                        w="1rem"
                                                                    />
                                                                }
                                                                size="xs"
                                                                onClick={(
                                                                    e
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    calendarActionClickHandler(
                                                                        "students",
                                                                        event.id
                                                                    );
                                                                }}
                                                            />
                                                        </AppTooltip>
                                                        <AppTooltip
                                                            label={t(
                                                                "padm.MyInternshipPage:text.option.assignStudents"
                                                            )}
                                                        >
                                                            <IconButton
                                                                aria-label="assignStudents"
                                                                variant={
                                                                    "unstyled"
                                                                }
                                                                icon={
                                                                    <AppIcon
                                                                        name="icl-user-plus"
                                                                        w="1rem"
                                                                    />
                                                                }
                                                                size="xs"
                                                                onClick={(
                                                                    e
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    calendarActionClickHandler(
                                                                        "assignStudents",
                                                                        event.id
                                                                    );
                                                                }}
                                                            />
                                                        </AppTooltip>
                                                    </HStack>
                                                );
                                            }}
                                        />
                                    ),
                                }}
                                onSelectEvent={handleSelectEvent}
                                messages={messages}
                                formats={formats}
                                resizable
                                // views={["month", "day", "agenda"]}
                            />
                        </Box>
                    ) : (
                        <>
                            {list && list.length > 0 && (
                                <VStack p={6}>
                                    <ListComponent
                                        table={table}
                                        paging={paging}
                                        list={list}
                                        loadingList={loadingList}
                                        onActionClick={actionClickHandler}
                                    />
                                </VStack>
                            )}
                        </>
                    )}
                    {formContainerState.isOpen && (
                        <FormComponent
                            formContainerState={formContainerState}
                            isDetailPage={isDetailPage}
                            entityType={"InternshipScheduleSeat"}
                        />
                    )}
                </>
            </AppPageContent>
        </>
    );
};
